import { trans } from "../../functions";
import noUiSlider from "nouislider";
import restaurantBanner from "../../../images/restaurant-supplies-banner.webp";
import ProductCardSkeleton from "./index/ProductCardSkeleton.vue";

export default {
    components: { VPagination: () => import("../VPagination.vue"), ProductCardSkeleton },
    props: ["initialQuery", "initialBrandName", "initialBrandBanner", "initialBrandSlug",
        "initialCategoryName", "initialCategoryBanner", "initialCategorySlug", "initialTagName",
        "initialTagSlug", "initialAttribute", "minPrice", "maxPrice", "initialSort",
        "initialPerPage", "initialPage", "initialViewMode", "initialCustomType"],
    data() {
        return {
            restaurantBanner,
            fetchingProducts: true,
            fetchError: false,
            hasLoaded: false,
            requestSequence: 0,
            products: { data: [], total: 0 },
            attributeFilters: [],
            brandBanner: this.initialBrandBanner,
            categoryName: this.initialCategoryName,
            categoryBanner: this.initialCategoryBanner,
            viewMode: this.initialViewMode,
            filtersOpen: false,
            queryParams: {
                query: this.initialQuery, brand: this.initialBrandSlug, customType: this.initialCustomType,
                category: this.initialCategorySlug, tag: this.initialTagSlug,
                attribute: Array.isArray(this.initialAttribute) ? {} : { ...this.initialAttribute },
                fromPrice: this.minPrice, toPrice: this.maxPrice,
                sort: this.initialSort, perPage: this.initialPerPage, page: this.initialPage,
            },
        };
    },
    computed: {
        showRestaurantBanner() {
            return !this.brandBanner && !this.queryParams.brand && !this.categoryBanner &&
                ["\u0645\u0633\u062a\u0644\u0632\u0645\u0627\u062a-\u0627\u0644\u0645\u0637\u0627\u0639\u0645", "restaurant-supplies"].includes(this.queryParams.category);
        },
        emptyProducts() { return this.products.data.length === 0; },
        totalPage() { return Math.ceil(this.products.total / this.queryParams.perPage); },
        showingResults() {
            return this.emptyProducts ? "" : trans("storefront::products.showing_results", {
                from: this.products.from, to: this.products.to, total: this.products.total,
            });
        },
        productCount() { return trans("storefront::categories.product_count", { count: this.products.total }); },
        priceFiltered() {
            return Number(this.queryParams.fromPrice) !== Number(this.minPrice) ||
                Number(this.queryParams.toPrice) !== Number(this.maxPrice);
        },
        activeFilters() {
            const filters = [];
            if (this.queryParams.customType) filters.push({ key: "customType", label: trans(
                this.queryParams.customType === "promo" ? "storefront::categories.offers" : "storefront::categories.new_products") });
            if (this.priceFiltered) filters.push({ key: "price", label: `${this.queryParams.fromPrice} - ${this.queryParams.toPrice}` });
            Object.entries(this.queryParams.attribute).forEach(([slug, values]) => {
                const attribute = this.attributeFilters.find((entry) => entry.slug === slug);
                values.forEach((value) => filters.push({ key: `attribute:${slug}:${value}`, slug, value,
                    label: `${attribute ? attribute.name + ': ' : ''}${value}` }));
            });
            return filters;
        },
    },
    mounted() {
        this.initPriceFilter();
        this.fetchProducts();
        if (this.$refs.latestProducts) this.initLatestProductsSlider();
        this.onViewportChange = () => { if (window.innerWidth >= 992 && this.filtersOpen) this.closeFilters(); };
        window.addEventListener("resize", this.onViewportChange);
    },
    beforeDestroy() {
        this.requestSequence++;
        if (this.abortController) this.abortController.abort();
        if (this.$refs.priceRange && this.$refs.priceRange.noUiSlider) this.$refs.priceRange.noUiSlider.destroy();
        window.removeEventListener("resize", this.onViewportChange);
        this.closeFilters(false);
    },
    methods: {
        initPriceFilter() {
            if (Number(this.maxPrice) <= Number(this.minPrice)) return;
            noUiSlider.create(this.$refs.priceRange, {
                connect: true, direction: window.AyanCart.rtl ? "rtl" : "ltr",
                start: [this.minPrice, this.maxPrice], range: { min: Number(this.minPrice), max: Number(this.maxPrice) },
                handleAttributes: [
                    { "aria-label": trans("storefront::categories.min_price") },
                    { "aria-label": trans("storefront::categories.max_price") },
                ],
            });
            this.$refs.priceRange.noUiSlider.on("update", (values, handle) => {
                this.queryParams[handle === 0 ? "fromPrice" : "toPrice"] = Number(Number(values[handle]).toFixed(2));
            });
            this.$refs.priceRange.noUiSlider.on("change", () => this.applyFilters());
        },
        updatePriceRange(fromPrice, toPrice) {
            const lower = fromPrice === null ? Number(this.queryParams.fromPrice) : Number(fromPrice);
            const upper = toPrice === null ? Number(this.queryParams.toPrice) : Number(toPrice);
            const from = Math.min(Number(this.maxPrice), Math.max(Number(this.minPrice), Number.isFinite(lower) ? lower : Number(this.minPrice)));
            const to = Math.min(Number(this.maxPrice), Math.max(from, Number.isFinite(upper) ? upper : Number(this.maxPrice)));
            this.queryParams.fromPrice = from;
            this.queryParams.toPrice = to;
            if (this.$refs.priceRange.noUiSlider) this.$refs.priceRange.noUiSlider.set([from, to]);
            this.applyFilters();
        },
        applyFilters(options) { this.queryParams.page = 1; this.fetchProducts(options); },
        setCollection(type) { this.queryParams.customType = type; this.applyFilters(); },
        toggleAttributeFilter(slug, value) {
            if (!this.queryParams.attribute[slug]) this.$set(this.queryParams.attribute, slug, []);
            const values = this.queryParams.attribute[slug];
            const index = values.indexOf(value);
            if (index >= 0) values.splice(index, 1); else values.push(value);
            if (!values.length) this.$delete(this.queryParams.attribute, slug);
            this.applyFilters({ updateAttributeFilters: false });
        },
        isFilteredByAttribute(slug, value) { return (this.queryParams.attribute[slug] || []).includes(value); },
        removeFilter(filter) {
            if (filter.slug) return this.toggleAttributeFilter(filter.slug, filter.value);
            if (filter.key === "price") return this.updatePriceRange(this.minPrice, this.maxPrice);
            this.setCollection("");
        },
        clearFilters() {
            this.queryParams.attribute = {};
            this.queryParams.customType = "";
            this.updatePriceRange(this.minPrice, this.maxPrice);
        },
        changeCategory(category) {
            this.categoryName = category.name;
            this.categoryBanner = category.banner ? category.banner.path : "";
            this.queryParams.query = null;
            this.queryParams.category = category.slug;
            this.queryParams.attribute = {};
            this.applyFilters();
        },
        changePage(page) {
            this.queryParams.page = page;
            this.fetchProducts();
            this.$refs.results.scrollIntoView({ block: "start" });
        },
        async fetchProducts(options = {}) {
            const sequence = ++this.requestSequence;
            if (this.abortController) this.abortController.abort();
            this.abortController = new AbortController();
            this.fetchingProducts = true;
            this.fetchError = false;
            try {
                const response = await axios.get(route("products.index", this.queryParams), { signal: this.abortController.signal });
                if (sequence !== this.requestSequence) return;
                this.products = response.data.products;
                this.hasLoaded = true;
                if (options.updateAttributeFilters !== false) this.attributeFilters = response.data.attributes;
            } catch (error) {
                if (sequence === this.requestSequence && error.code !== "ERR_CANCELED") this.fetchError = true;
            } finally {
                if (sequence === this.requestSequence) this.fetchingProducts = false;
            }
        },
        openFilters() {
            this.filterTrigger = document.activeElement;
            this.previousOverflow = document.body.style.overflow;
            document.body.style.overflow = "hidden";
            this.filtersOpen = true;
            // Inert the rest of the storefront without duplicating filter controls or their IDs.
            this.inertElements = [];
            let branch = this.$refs.filterPanel;
            while (branch && branch !== document.body) {
                Array.from(branch.parentElement.children).forEach((element) => {
                    if (element !== branch && element !== this.$refs.filterBackdrop && !element.inert) {
                        element.inert = true;
                        this.inertElements.push(element);
                    }
                });
                branch = branch.parentElement;
            }
            this.$nextTick(() => this.$refs.filterClose.focus());
        },
        closeFilters(restoreFocus = true) {
            if (!this.filtersOpen) return;
            this.filtersOpen = false;
            document.body.style.overflow = this.previousOverflow || "";
            (this.inertElements || []).forEach((element) => { element.inert = false; });
            if (restoreFocus && this.filterTrigger) this.filterTrigger.focus();
        },
        trapFilterFocus(event) {
            if (!this.filtersOpen) return;
            if (event.key === "Escape") { event.preventDefault(); this.closeFilters(); return; }
            if (event.key !== "Tab") return;
            const controls = Array.from(this.$refs.filterPanel.querySelectorAll('button, a[href], input, select, summary, [tabindex="0"]'))
                .filter((element) => !element.disabled && element.getClientRects().length);
            const first = controls[0], last = controls[controls.length - 1];
            if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
            else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
        },
        initLatestProductsSlider() {
            $(this.$refs.latestProducts).slick({ rows: 0, dots: false, arrows: true, infinite: true,
                slidesToShow: 1, slidesToScroll: 1, rtl: window.AyanCart.rtl });
        },
    },
};
